import React, { useContext, useEffect, useState } from "react";
import { css } from "@emotion/react";
import { Helmet } from "react-helmet";
import { AmplitudeContext } from "../context/amplitude";
import { FirebaseContext } from "../context/firebase";
import { useDispatch, useSelector } from "react-redux";
import {
  getFeaturedJobs,
  selectFeaturedJobPosts,
  selectFeaturedJobsHasFulfilled,
} from "../context/slices/jobs";
import { FEATURED_JOB_SET } from "../utl/constants";

import Layout from "../templates/siteLayout";
import Seo from "../components/seo";
import Button from "../components/button";
import FeaturedJob from "../components/featuredJob";
import LoadingTile from "../components/loadingTile";

import { EastRounded } from "@mui/icons-material";
import { baseColor2, logoColor2 } from "../styles/color";
import { breakpointMd } from "../styles/layout";

import AvatarGrid from "../images/avatar-grid.svg";
import Popup from "../components/popup";
import RevueNewsletter from "../components/revueNewsletter";

const ctaStyles = css`
  display: grid;
  gap: 3rem 2rem;
  padding: 1rem 0;

  h2 {
    margin-top: 0;
  }

  .cta-button {
    margin-top: 2rem;
  }
`;

const featuredJobStyles = css`
  display: grid;
  gap: 0.5rem;

  @media only screen and (min-width: ${breakpointMd}) {
    gap: 0.75rem;
  }
`;

const Home = () => {
  const monitor = useContext(AmplitudeContext);
  monitor.logEvent("SITE HOME PAGE VIEWED");

  const dispatch = useDispatch();
  const fb = useContext(FirebaseContext);
  const [isFetched, setIsFetched] = useState(
    // fb is undefined at build time, use null
    fb === undefined ? null : fb.isFetched
  );
  const featuredJobsHasFulfilled = useSelector(selectFeaturedJobsHasFulfilled);
  const [featuredJobSet, setFeaturedJobSet] = useState(FEATURED_JOB_SET);
  const featuredJobPosts = useSelector((state) =>
    selectFeaturedJobPosts(state, featuredJobSet)
  );

  useEffect(() => {
    // Ensure we've fetched and activated remote config
    if (isFetched === null) {
      // Fetch and activate the new values
      fb.remoteConfig
        .fetchAndActivate()
        .then((status) => {
          // Update status so we don't fetch again
          fb.setIsFetched(status);
          setIsFetched(status);
        })
        .catch((_error) => {
          monitor.logError("pages/index.js", "0049");
        });
    } else if (!featuredJobsHasFulfilled) {
      // Get featured job set number from Remote Config
      const jobSet = fb.remoteConfig.getNumber("featuredJobSet");

      // Update state so we select the correct jobs from Redux post-fetch
      setFeaturedJobSet(jobSet);

      // Fetch featured jobs from Firestore
      dispatch(getFeaturedJobs(jobSet));
    } else if (featuredJobsHasFulfilled) {
      // Update local state to pull the correct jobs
      setFeaturedJobSet(fb.remoteConfig.getNumber("featuredJobSet"));
    }
  }, [
    dispatch,
    fb,
    isFetched,
    featuredJobsHasFulfilled,
    featuredJobPosts,
    monitor,
  ]);

  return (
    <Layout>
      <Seo
        title="Find the most meaningful work of your career"
        description="One Work is a job search and hiring platform for careers with social and environmental impact. We connect mission-driven talent to companies making the world a better place. (Hey, that sounds like you!)"
      />
      {/* Structured data to support SEO */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            url: "https://onework.co",
            logo: "https://firebasestorage.googleapis.com/v0/b/join-one-work.appspot.com/o/assets%2Fjoin%2Fimages%2Fblue-square-logo%402x.png?alt=media&token=4d3d5070-1249-4fb4-b4e0-18cbb1a2bd48",
          })}
        </script>
      </Helmet>

      <div className="material-section">
        <section className="grid-item-100">
          <div className="hero">
            <div className="hero-left">
              <Popup />
              <h1 className="hero-headline">
                Find <span className="green">the</span>{" "}
                <span className="pink">most</span>{" "}
                <span className="yellow">meaningful</span>{" "}
                <span className="blue">work</span> of your career.
              </h1>
              <div className="hero-cta"></div>
            </div>
            <div className="hero-right">
              <img
                className="hero-img"
                src={AvatarGrid}
                alt="four illustrated people in bright clothes smiling"
              />
            </div>
          </div>
        </section>
        <section
          className="grid-item-100"
          css={css`
            margin: -1rem 0 2.5rem 0;
          `}
        >
          <p className="big-paragraph">
            Subscribe to our newsletter to be the first to learn about new
            social impact job opportunities:
          </p>
          <RevueNewsletter />
        </section>
      </div>

      <div className="white-bg">
        <div className="material-section">
          <section className="grid-item-100">
            <h2>Featured job postings</h2>
            <p>
              New jobs are added each week! Check out the latest jobs featured
              in our newsletter:
            </p>
            {featuredJobsHasFulfilled ? (
              <div css={featuredJobStyles}>
                {featuredJobPosts.map((job, k) => {
                  return <FeaturedJob key={k} jobData={job} />;
                })}
              </div>
            ) : (
              <div css={featuredJobStyles}>
                {[...Array(5).keys()].map((k) => (
                  <LoadingTile key={k} aspectRatio="10 / 1" />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      <div className="white-bg">
        <div className="material-section py-2">
          <section className="grid-item-100">
            <div css={ctaStyles}>
              <div>
                <h2>Opportunity in your inbox</h2>
                <p>
                  Subscribe to our newsletter for curated reads and job
                  opportunities to inspire and advance social impact.
                </p>
                <div className="cta-button">
                  <a
                    href="https://www.getrevue.co/profile/joinonework"
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Button
                      text="Send me jobs"
                      icon={<EastRounded />}
                      bgColor={logoColor2}
                      color={baseColor2}
                      onClick={() => {
                        monitor.logEvent(
                          "NEWSLETTER NAVIGATION BUTTON CLICKED",
                          {
                            buttonLocation: "home page",
                          }
                        );
                      }}
                    />
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default Home;
